import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { initializeApp, getApps, deleteApp } from 'firebase/app';
import { getAuth, RecaptchaVerifier, signInWithPhoneNumber } from 'firebase/auth';
import axios from 'axios';
import { API_URL } from '../../config/api';
import './PhoneOTPModal.css';

let firebaseApp = null;

function getFirebaseApp(config) {
  // Clean up existing app if config changed
  const existing = getApps().find(a => a.name === 'smsOtp');
  if (existing) {
    const currentConfig = existing.options;
    if (currentConfig.apiKey !== config.apiKey || currentConfig.projectId !== config.projectId) {
      deleteApp(existing);
      firebaseApp = null;
    } else {
      firebaseApp = existing;
      return firebaseApp;
    }
  }
  firebaseApp = initializeApp(config, 'smsOtp');
  return firebaseApp;
}

const PhoneOTPModal = ({ isOpen, onClose, onVerified, userId }) => {
  const [step, setStep] = useState('phone'); // phone | otp | success
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [configLoading, setConfigLoading] = useState(true);
  const [firebaseConfig, setFirebaseConfig] = useState(null);
  const [confirmationResult, setConfirmationResult] = useState(null);
  const [countdown, setCountdown] = useState(0);

  const otpRefs = useRef([]);
  const recaptchaRef = useRef(null);
  const recaptchaVerifierRef = useRef(null);

  // Fetch Firebase config from backend
  useEffect(() => {
    if (!isOpen) return;
    const fetchConfig = async () => {
      try {
        const res = await axios.get(`${API_URL}/sms-verification/config`);
        if (res.data.enabled && res.data.firebaseConfig) {
          setFirebaseConfig(res.data.firebaseConfig);
        } else {
          setError('SMS verification is not configured. Please contact the administrator.');
        }
      } catch {
        setError('Could not load SMS verification settings.');
      } finally {
        setConfigLoading(false);
      }
    };
    fetchConfig();
  }, [isOpen]);

  // Countdown timer for resend
  useEffect(() => {
    if (countdown <= 0) return;
    const timer = setTimeout(() => setCountdown(c => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [countdown]);

  // Cleanup recaptcha on unmount
  useEffect(() => {
    return () => {
      if (recaptchaVerifierRef.current) {
        try { recaptchaVerifierRef.current.clear(); } catch { /* ignore */ }
        recaptchaVerifierRef.current = null;
      }
    };
  }, []);

  const setupRecaptcha = useCallback(() => {
    if (!firebaseConfig) return null;
    const app = getFirebaseApp(firebaseConfig);
    const auth = getAuth(app);
    auth.languageCode = 'en';

    if (recaptchaVerifierRef.current) {
      try { recaptchaVerifierRef.current.clear(); } catch { /* ignore */ }
    }

    const verifier = new RecaptchaVerifier(auth, recaptchaRef.current, {
      size: 'invisible',
      callback: () => {},
      'expired-callback': () => {
        setError('reCAPTCHA expired. Please try again.');
      }
    });

    recaptchaVerifierRef.current = verifier;
    return { auth, verifier };
  }, [firebaseConfig]);

  const handleSendOTP = async (e) => {
    e.preventDefault();
    setError('');

    if (!phone.trim()) {
      setError('Please enter a phone number');
      return;
    }

    // Ensure phone starts with +
    let formattedPhone = phone.trim();
    if (!formattedPhone.startsWith('+')) {
      setError('Phone number must start with country code (e.g. +971...)');
      return;
    }

    setLoading(true);

    try {
      const setup = setupRecaptcha();
      if (!setup) {
        setError('Firebase not configured');
        setLoading(false);
        return;
      }

      const { auth, verifier } = setup;
      const result = await signInWithPhoneNumber(auth, formattedPhone, verifier);
      setConfirmationResult(result);
      setStep('otp');
      setCountdown(60);
    } catch (err) {
      console.error('Send OTP error:', err);
      if (err.code === 'auth/invalid-phone-number') {
        setError('Invalid phone number format');
      } else if (err.code === 'auth/too-many-requests') {
        setError('Too many attempts. Please try again later.');
      } else if (err.code === 'auth/captcha-check-failed') {
        setError('reCAPTCHA verification failed. Please refresh and try again.');
      } else {
        setError(err.message || 'Failed to send OTP');
      }
      // Reset recaptcha on error
      if (recaptchaVerifierRef.current) {
        try { recaptchaVerifierRef.current.clear(); } catch { /* ignore */ }
        recaptchaVerifierRef.current = null;
      }
    } finally {
      setLoading(false);
    }
  };

  const handleOTPChange = (index, value) => {
    if (value.length > 1) {
      // Handle paste
      const digits = value.replace(/\D/g, '').split('').slice(0, 6);
      const newOtp = [...otp];
      digits.forEach((d, i) => {
        if (index + i < 6) newOtp[index + i] = d;
      });
      setOtp(newOtp);
      const nextIndex = Math.min(index + digits.length, 5);
      otpRefs.current[nextIndex]?.focus();
      return;
    }

    if (value && !/^\d$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < 5) {
      otpRefs.current[index + 1]?.focus();
    }
  };

  const handleOTPKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setError('');

    const otpCode = otp.join('');
    if (otpCode.length !== 6) {
      setError('Please enter the 6-digit OTP');
      return;
    }

    if (!confirmationResult) {
      setError('OTP session expired. Please resend OTP.');
      return;
    }

    setLoading(true);

    try {
      const credential = await confirmationResult.confirm(otpCode);
      const idToken = await credential.user.getIdToken();

      // Verify on backend and link to user if logged in
      const token = localStorage.getItem('token');
      const endpoint = token ? '/sms-verification/verify-and-link' : '/sms-verification/verify-otp';
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const res = await axios.post(`${API_URL}${endpoint}`, {
        idToken,
        phone: phone.trim()
      }, { headers });

      if (res.data.verified) {
        setStep('success');
        setTimeout(() => {
          if (onVerified) onVerified(phone.trim());
          onClose();
        }, 1500);
      } else {
        setError('Verification failed. Please try again.');
      }
    } catch (err) {
      console.error('Verify OTP error:', err);
      if (err.code === 'auth/invalid-verification-code') {
        setError('Invalid OTP. Please check and try again.');
      } else if (err.code === 'auth/code-expired') {
        setError('OTP expired. Please resend.');
      } else {
        setError(err.response?.data?.error || err.message || 'Verification failed');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = () => {
    if (countdown > 0) return;
    setOtp(['', '', '', '', '', '']);
    setStep('phone');
    setConfirmationResult(null);
    setError('');
  };

  const handleClose = () => {
    setStep('phone');
    setPhone('');
    setOtp(['', '', '', '', '', '']);
    setError('');
    setConfirmationResult(null);
    setCountdown(0);
    onClose();
  };

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div className="otp-modal-overlay" onClick={handleClose}>
      <div className="otp-modal" onClick={e => e.stopPropagation()}>
        <button className="otp-modal-close" onClick={handleClose}>
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M15 5L5 15M5 5l10 10" stroke="#666" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        </button>

        <div className="otp-modal-icon">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#1a3a5c" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <rect x="5" y="2" width="14" height="20" rx="2" ry="2"/>
            <line x1="12" y1="18" x2="12.01" y2="18"/>
          </svg>
        </div>

        {configLoading ? (
          <div className="otp-loading">Loading...</div>
        ) : step === 'phone' ? (
          <>
            <h2 className="otp-title">Verify Phone Number</h2>
            <p className="otp-subtitle">Enter your phone number with country code to receive an OTP</p>

            <form onSubmit={handleSendOTP} className="otp-form">
              <div className="otp-phone-input">
                <input
                  type="tel"
                  value={phone}
                  onChange={e => setPhone(e.target.value)}
                  placeholder="+971 50 123 4567"
                  className={error ? 'error' : ''}
                  autoFocus
                />
              </div>

              {error && <div className="otp-error">{error}</div>}

              <button type="submit" className="otp-btn-primary" disabled={loading}>
                {loading ? 'Sending OTP...' : 'Send OTP'}
              </button>
            </form>
          </>
        ) : step === 'otp' ? (
          <>
            <h2 className="otp-title">Enter OTP</h2>
            <p className="otp-subtitle">
              We sent a 6-digit code to <strong>{phone}</strong>
            </p>

            <form onSubmit={handleVerifyOTP} className="otp-form">
              <div className="otp-inputs">
                {otp.map((digit, i) => (
                  <input
                    key={i}
                    ref={el => otpRefs.current[i] = el}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={e => handleOTPChange(i, e.target.value)}
                    onKeyDown={e => handleOTPKeyDown(i, e)}
                    className={`otp-digit ${error ? 'error' : ''}`}
                    autoFocus={i === 0}
                  />
                ))}
              </div>

              {error && <div className="otp-error">{error}</div>}

              <button type="submit" className="otp-btn-primary" disabled={loading}>
                {loading ? 'Verifying...' : 'Verify OTP'}
              </button>

              <div className="otp-resend">
                {countdown > 0 ? (
                  <span className="otp-resend-timer">Resend OTP in {countdown}s</span>
                ) : (
                  <button type="button" className="otp-resend-btn" onClick={handleResendOTP}>
                    Resend OTP
                  </button>
                )}
              </div>
            </form>
          </>
        ) : (
          <div className="otp-success">
            <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
              <circle cx="32" cy="32" r="30" stroke="#4CAF50" strokeWidth="3" fill="#E8F5E9"/>
              <path d="M20 32l8 8 16-16" stroke="#4CAF50" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <h2>Phone Verified!</h2>
            <p>Your phone number has been verified successfully.</p>
          </div>
        )}

        <div id="recaptcha-container" ref={recaptchaRef}></div>
      </div>
    </div>,
    document.body
  );
};

export default PhoneOTPModal;
